import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { FileSignature, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
};

const daysSince = (iso) => {
    if (!iso) return null;
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    return diff < 0 ? 0 : diff;
};

export const DocumentsPendingAckWidget = () => {
    const [data, setData] = useState({ total: 0, items: [] });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/documents/pending-ack")
            .then((r) => mounted && setData(r.data))
            .catch(() => {})
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="documents-pending-ack-widget">
                <div className="flex h-24 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="documents-pending-ack-widget">
            <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                    <FileSignature className="h-4 w-4 text-[#376DB3]" />
                    Documenti da firmare
                    {data.total > 0 && (
                        <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white" data-testid="documents-pending-badge">
                            {data.total}
                        </span>
                    )}
                </h2>
                {data.total > 0 && (
                    <Link to="/documenti/da-firmare" className="text-xs font-semibold text-[#376DB3] hover:underline" data-testid="documents-pending-view-all">
                        Vedi tutti →
                    </Link>
                )}
            </div>
            {data.total === 0 ? (
                <p className="mt-3 flex items-center gap-2 text-sm text-slate-500" data-testid="documents-pending-empty">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    Nessun documento in attesa di presa visione.
                </p>
            ) : (
                <div className="mt-3 space-y-2">
                    {data.items.slice(0, 5).map((it) => {
                        const days = daysSince(it.uploaded_at);
                        return (
                            <Link
                                key={it.document_id}
                                to={`/dipendenti/${it.employee_id}`}
                                className="flex items-center justify-between rounded-md border border-amber-100 bg-amber-50 px-3 py-2 text-sm hover:bg-amber-100"
                                data-testid={`document-pending-${it.document_id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium text-slate-700">{it.employee_label}</div>
                                    <div className="truncate text-xs text-slate-500" title={it.title || it.filename}>
                                        {it.category_label} · {it.title || it.filename} · {fmtDate(it.uploaded_at)}
                                        {days !== null && (
                                            <span className={days >= 7 ? " font-semibold text-amber-700" : ""}>
                                                {" "}· in attesa da {days === 0 ? "oggi" : `${days} gg`}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                            </Link>
                        );
                    })}
                    {data.total > 5 && (
                        <Link to="/documenti/da-firmare" className="block rounded-md border border-[#376DB3]/30 bg-[#376DB3]/5 px-3 py-1.5 text-center text-[11px] font-semibold text-[#376DB3] hover:bg-[#376DB3]/10" data-testid="documents-pending-more-link">
                            e altri {data.total - 5} — vedi tutti →
                        </Link>
                    )}
                </div>
            )}
        </section>
    );
};
